// The wizard's optional htaccess section: two textareas turned into `spec.htaccess` (crd/README "htaccess").
// The contract passes `htaccess` through unchanged, so the CRD's own validation (422 Invalid, verbatim) is
// the authority; this parser only turns lines into objects and reports lines it cannot read.

export interface JamesEntry {
  website: string;
  url: string;
  shopUrl?: string;
  newCheckout?: boolean;
  newGuestcard?: boolean;
  newProfile?: boolean;
}

export interface ParseResult<T> {
  value: T;
  errors: string[];
}

const FLAGS: Record<string, keyof Pick<JamesEntry, 'newCheckout' | 'newGuestcard' | 'newProfile'>> = {
  checkout: 'newCheckout',
  guestcard: 'newGuestcard',
  profile: 'newProfile',
};

/**
 * One line per James website: `<store stem> <james url> [shop=<url>] [checkout] [guestcard] [profile]`.
 * The store stem `-` stands for "" (the plain language stores). A JSON array is accepted as well.
 * Empty lines and lines starting with `#` are ignored.
 */
export function parseJames(text: string): ParseResult<JamesEntry[]> {
  const trimmed = text.trim();
  if (!trimmed) return { value: [], errors: [] };
  if (trimmed.startsWith('[')) {
    try {
      const v = JSON.parse(trimmed) as unknown;
      if (!Array.isArray(v)) return { value: [], errors: ['The JSON must be a list of James entries.'] };
      return { value: v as JamesEntry[], errors: [] };
    } catch (e) {
      return { value: [], errors: [`The JSON does not parse: ${(e as Error).message}`] };
    }
  }
  const value: JamesEntry[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();
  text.split('\n').forEach((raw, i) => {
    const line = raw.trim();
    if (!line || line.startsWith('#')) return;
    const [stem, url, ...opts] = line.split(/\s+/);
    if (!stem || !url) {
      errors.push(`Line ${i + 1}: expected "<store stem> <james url>", got "${line}".`);
      return;
    }
    const entry: JamesEntry = { website: stem === '-' ? '' : stem, url };
    for (const o of opts) {
      if (o.startsWith('shop=')) entry.shopUrl = o.slice(5);
      else if (FLAGS[o]) entry[FLAGS[o]] = true;
      else errors.push(`Line ${i + 1}: unknown option "${o}" (use shop=<url>, checkout, guestcard, profile).`);
    }
    if (seen.has(entry.website)) errors.push(`Line ${i + 1}: store stem "${stem}" appears twice.`);
    seen.add(entry.website);
    value.push(entry);
  });
  return { value, errors };
}

/** `spec.htaccess` from the two textareas, or undefined when both are empty (CORS origins only). */
export function buildHtaccess(jamesText: string, extraRules: string): ParseResult<Record<string, unknown> | undefined> {
  const james = parseJames(jamesText);
  const out: Record<string, unknown> = {};
  if (james.value.length) out.james = james.value;
  if (extraRules.trim()) out.extraRules = extraRules.endsWith('\n') ? extraRules : `${extraRules}\n`;
  return { value: Object.keys(out).length ? out : undefined, errors: james.errors };
}

/** Hostnames from a textarea or a comma list. */
export function parseHosts(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((h) => h.trim())
    .filter(Boolean);
}
